/**
 * Resolves client ticket-create permission using the caller's authenticated
 * Supabase client (respects RLS on access tables). Safe for browser + server.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

import {
  evaluateClientTicketCreateAccess,
  normalizeAccessGrantRows,
  type AccessGrantRow,
  type ClientTicketCreateDenialReason,
} from "@/lib/auth/client-ticket-create-eval";

export async function resolveClientTicketCreateDenialReason(
  supabase: SupabaseClient,
  userId: string,
  contactId: string,
): Promise<ClientTicketCreateDenialReason> {
  if (!userId || !contactId) return "missing_contact";

  const { data: contactGrants, error: contactErr } = await supabase
    .from("contact_user_access")
    .select("access_level, is_active, expires_at")
    .eq("user_id", userId)
    .eq("contact_id", contactId);

  if (contactErr) return "lookup_failed";

  const { data: contactRow, error: contactLookupErr } = await supabase
    .from("contacts")
    .select("company_id")
    .eq("id", contactId)
    .maybeSingle();

  if (contactLookupErr) return "lookup_failed";

  const companyId = (contactRow as { company_id: string | null } | null)?.company_id ?? null;
  let companyGrants: AccessGrantRow[] = [];

  if (companyId) {
    const { data: companyGrantRows, error: companyErr } = await supabase
      .from("company_user_access")
      .select("access_level, is_active, expires_at")
      .eq("user_id", userId)
      .eq("company_id", companyId);

    if (companyErr) return "lookup_failed";
    companyGrants = normalizeAccessGrantRows(companyGrantRows);
  }

  return evaluateClientTicketCreateAccess({
    contactGrants: normalizeAccessGrantRows(contactGrants),
    companyGrants,
    companyId,
  });
}
